import styled from '@emotion/styled'

import sanitizeHtml from 'sanitize-html'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import type { PageConfig } from '@segment/contexts/page'
import { usePageConfig } from '@segment/contexts/page'

const getCopy = (pageConfig: PageConfig) => ({
  heading: `Move quickly without compromise`,
  description: [
    {
      paragraph: `Businesses need the building blocks to move quickly, along with the flexibility to customize their data infrastructure to match their needs. Developers trust Segment because we have <a href="https://segment.com/docs/">extensive documentation</a>, <a href="${getAbsoluteUrl(
        `/product/developer-toolkit/`,
        pageConfig
      )}">open-source tools</a>, and <a href="${getAbsoluteUrl(
        `/recipes/`,
        pageConfig
      )}">pre-built recipes</a> at your disposal. For instance, <a href="https://segment.com/docs/connections/functions/source-functions/">Source Functions</a> ensures you can collect data from any third-party app, while our <a href="https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/">Analytics.js 2.0</a> and <a href="https://segment.com/docs/connections/sources/catalog/#mobile">mobile SDKs</a> enable developers to augment events before or after they occur, or even when they’re in-flight.`,
    },
    {
      paragraph: `Segment’s low cost composable CDP has deep integrations with every major data warehouse and robust data portability. Simplify your data stack and eliminate the need to spend years procuring or building your own bespoke solution with the platform that has spent over a decade building the leading customer data platform.`,
    },
    {
      paragraph: `And, for those companies that want a complete solution, Segment allows you to choose between composable components or active our full platform with customer data infrastructure, ETL, identity resolution, profile orchestration, multi-channel activation, data governance and privacy compliance, all in one real-time platform.`,
    },
  ],
})

const MoveQuickly = () => {
  const pageConfig = usePageConfig()
  const copy = getCopy(pageConfig)
  return (
    <StyledSectionBase variant="centered">
      <Section.Copy>
        <StyledCopyTextBase>
          <CopyText.Heading tag="h2" variant="h2">
            {copy.heading}
          </CopyText.Heading>
          {copy.description.map((text, index) => (
            <CopyText.Description
              key={index}
              tag="p"
              variant="pLarge"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(text.paragraph, { allowedTags: [`a`] }),
              }}
            />
          ))}
        </StyledCopyTextBase>
      </Section.Copy>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-bottom: 0;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      padding-bottom: 40px;
    }
  }
`

const StyledCopyTextBase = styled(CopyTextBase)`
  text-align: start;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    text-align: center;
  }
`

export default MoveQuickly
